import {
  CoherenceDataGrid,
} from "@coherence-design-system/controls";
import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import {
  ActionButton,
  IIconProps,
  Stack,
  Selection,
  SelectionMode,
  MessageBar,
  MessageBarType,
  SearchBox,
  Separator,
  TooltipHost,
  TooltipDelay,
  DirectionalHint,
  ITooltipHostStyles,
  Spinner,
  SpinnerSize,
  Icon,
  Dialog,
  DialogType,
  DialogFooter,
  TextField,
  PrimaryButton,
  DefaultButton,
  IColumn,
  KeyCodes,
  ITextField,
  ContextualMenu,
  IContextualMenuProps,
} from "@fluentui/react";
import { appInsights } from "../../services/Logging/AppInsights";
import { QuotationService } from "../../services/Quotations/QuotationService";
import { QuoteSessionTracker } from "../../Utils/QuoteSessionTracker";
import { ActivityTimingTracker } from "../../Utils/ActivityTimingTracker";
import { useNavigate } from "react-router-dom";
import {
  IQuotationPayload,
  IQuotationResponse,
  IResponseMessage,
  QuotationInfoType,
} from "./Quotation.types";
import {
  DefaultCurrentPage,
  DefaultCurrentPageSize,
  DefaultTotalPages,
  EDIT_ACTION,
  InitialQuotationInfo,
  InitialStateOfResponseMessage,
  NEW_ACTION,
  QuotationStatus,
} from "../Quotation/Common/QuotationData";
import { QuotationContext } from "./QuotationContext";
import PaginationComponent from "./Components/PaginationComponent";
import {
  CustomSpanForListing,
  searchBoxIconWidthStyles,
  searchBoxWidthStyles,
  searchIconStyle,
} from "./QuotationStyle";
import { useBoolean } from "@fluentui/react-hooks";
import { SignQuotationComponent } from "./Components/SignQuotationComponent/SignQuotationComponent";
import { DialogComponent } from "./Components/DialogComponent";
import { useLocalStorageContext } from "../../Provider/LocalStorageProvider";
import { UserRoles } from "../../common/enum/UserRoles";
import { fetchLoggedInUser } from "../../Utils/FetchLoggedInUser";
import { IUserInfo } from "../../common/Interfaces/IUserInfo";
import { CustomerService } from "../../services/Customers/CustomerService";
import { formattedPrice } from "../../Utils/FormattedPrice";

// Helper to format date as DD-MM-YYYY
const formatDateDDMMYYYY = (dateString: string | Date | undefined): string => {
  if (!dateString) return "";
  const date = typeof dateString === "string" ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear());
  return `${day}-${month}-${year}`;
};

interface IQuotationsProps {
  status: string;
  searchText: string;
  onSearchTextChange: (text: string) => void;
}

const AddIcon: IIconProps = { iconName: "Add" };
const EditIcon: IIconProps = { iconName: "Edit" };
const CancelIcon: IIconProps = { iconName: "ErrorBadge" };
const SignatureIcon: IIconProps = { iconName: "InsertSignatureLine" };

export const QuotationList: React.FunctionComponent<IQuotationsProps> = (
  props
) => {
  const { setQuotationInfo } = useContext(
    QuotationContext
  ) as QuotationInfoType;
  const navigate = useNavigate();
  const { genericRole, userRole } = useLocalStorageContext();
  const [hasSignedSuccefully, setHasSignedSuccessfully] =
    useState<boolean>(false);
  const [quotationStatus, setQuotationStatus] = useState<string>(props.status);
  const [updatedList, setUpdatedList] = useState<any>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [searchTextTemp, setSearchTextTemp] = useState<string>("");
  const [selectedQuotationStatus, setSelectedQuotationStatus] =
    useState<string>();
  const [totalPages, setTotalPages] = useState<number>(DefaultTotalPages);
  const [currentPage, setCurrentPage] = useState<number>(DefaultCurrentPage);
  const [currentPageSize, setCurrentPageSize] = useState<number>(
    DefaultCurrentPageSize
  );
  
  // Column resize state and refs
  const [isResizeDialogHidden, setIsResizeDialogHidden] = useState<boolean>(true);
  const [focusedColumnIndex, setFocusedColumnIndex] = useState<number>(-1);
  const [columnDropdownElement, setColumnDropdownElement] = useState<HTMLElement | null>(null);
  const textfieldRef = useRef<ITextField>(null);
  const columnToEdit = useRef<IColumn | null>(null);
  const detailsListRef = useRef<any>(null);
  const [quotationData, setQuotationData] = useState([]);
  const [selectedGridData, setSelectedGridData] = useState<{
    items: any[];
  }>({ items: [] });
  const [quotationSuccessMessage, setQuotationSuccessMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [quotationFailureMessage, setQuotationFailureMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchResponseMessage, setSearchResponseMessage] =
    useState<string>("");

  const handleSearchSubmit = () => {
    setSearchText(searchTextTemp);
    sendSearchTextToParent(searchTextTemp);
  };
  const [isPanelOpen, { setTrue: openPanel, setFalse: dismissPanel }] =
    useBoolean(false);
  const [isOpenModal, { setTrue: openModal, setFalse: closeModal }] =
    useBoolean(false);
  const [userInfo, setUserInfo] = useState<IUserInfo>({
    id: "",
    displayName: "",
  });
  useEffect(() => {
    fetchLoggedInUser(setUserInfo);
  }, []);
  useEffect(() => {
    setTotalPages(DefaultTotalPages);
    setCurrentPage(DefaultCurrentPage);
    setCurrentPageSize(DefaultCurrentPageSize);
    setSelectedGridData({ items: [] });
    quotationSelection.setAllSelected(false);
    setQuotationStatus(props.status);
  }, [props.status]);

  useEffect(() => {
    getAllQuotation();
  }, [quotationStatus, currentPage, currentPageSize, searchText]);

  useEffect(() => {
    if (searchText) {
      quotationData.length === 0
        ? setSearchResponseMessage("No record found in search result.")
        : setSearchResponseMessage(
          `Record updated. ${quotationData.length} record found in search result.`
        );
    }
  }, [JSON.stringify(quotationData)]);

  useEffect(() => {
    if (hasSignedSuccefully) {
      getAllQuotation();
    }
  }, [hasSignedSuccefully]);
  
  // Add keyboard accessibility to column headers
  useEffect(() => {
    // Store event handlers for cleanup
    const eventHandlers: Array<{ element: HTMLElement, handler: (e: KeyboardEvent) => void }> = [];
    
    // Give time for the grid to render
    const timeout = setTimeout(() => {
      // Make column headers focusable and add keyboard event listeners
      const headerElements = document.querySelectorAll('[role="columnheader"]');
      
      headerElements.forEach((header, index) => {
        const element = header as HTMLElement;
        if (element) {
          element.tabIndex = 0; // Make focusable
          
          // Add new event listener
          const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.keyCode === KeyCodes.enter) {
              e.preventDefault();
              const column = updatedList[index-1];
              if (column) {
                columnToEdit.current = column;
                setColumnDropdownElement(element);
              }
            } else if (e.key === 'ArrowLeft' || e.keyCode === KeyCodes.left) {
              e.preventDefault();
              if (!e.shiftKey) {
                // Regular left arrow - navigate to previous column
                if (index > 0) {
                  const newIndex = index - 1;
                  (headerElements[newIndex] as HTMLElement)?.focus();
                }
              } else {
                // Shift+Left arrow - decrease column width
                const column = updatedList[index-1];
                if (column) {
                  resizeColumnDirectly(column, -20);
                }
              }
            } else if (e.key === 'ArrowRight' || e.keyCode === KeyCodes.right) {
              e.preventDefault();
              if (!e.shiftKey) {
                // Regular right arrow - navigate to next column
                if (index < headerElements.length - 1) {
                  const newIndex = index + 1;
                  (headerElements[newIndex] as HTMLElement)?.focus();
                }
              } else {
                // Shift+Right arrow - increase column width
                const column = updatedList[index-1];
                if (column) {
                  resizeColumnDirectly(column, 20);
                }
              }
            } else if (e.altKey && (e.key === 'ArrowUp' || e.keyCode === KeyCodes.up)) {
              e.preventDefault();
              console.log('Alt+Up Arrow pressed on column header:', index-1);
              const column = updatedList[index-1];
              if (column) {
                console.log('Sorting column ascending:', column.name);
                sortColumnData(column, 'asc');
              } else {
                console.log('Column not found in updatedList for index:', index-1);
              }
            } else if (e.altKey && (e.key === 'ArrowDown' || e.keyCode === KeyCodes.down)) {
              e.preventDefault();
              console.log('Alt+Down Arrow pressed on column header:', index-1);
              const column = updatedList[index-1];
              if (column) {
                console.log('Sorting column descending:', column.name);
                sortColumnData(column, 'desc');
              } else {
                console.log('Column not found in updatedList for index:', index-1);
              }
            }
          };
          
          // Store handler for cleanup
          eventHandlers.push({ element, handler: handleKeyDown });
          
          element.addEventListener('keydown', handleKeyDown);
        }
      });
    }, 500);
    
    // Cleanup
    return () => {
      clearTimeout(timeout);
      // Remove all event listeners
      eventHandlers.forEach(({ element, handler }) => {
        element.removeEventListener('keydown', handler);
      });
    };
  }, [updatedList, quotationData]);

  const getAllQuotation = () => {
    setIsLoading(true);
    appInsights.trackEvent({
      name: "In QuotationList.tsx, triggering get All Quotation API for selected status",
      properties: { status: quotationStatus },
    });
    const quotationPayload: IQuotationPayload = {
      status: quotationStatus,
      msContact: "SMB-CASE",
      records: currentPageSize,
      skip: (currentPage - 1) * currentPageSize,
      searchText: searchText,
    };
    quotationStatus &&
      QuotationService.getAllQuotation(quotationPayload)
        .then((res) => {
          setIsLoading(false);
          if (res !== undefined) {
            appInsights.trackEvent({
              name: "In QuotationList.tsx, quotation list feteched successfully.",
              properties: { status: quotationStatus },
            });
            const modifedData =
              res.data?.data?.quotations?.map(
                (x: IQuotationResponse, index: number) => ({
                  ...x,
                  key: index,
                })
              ) ?? [];
            const totalSize: number = parseInt(res?.data?.data?.totalSize);
            const totalPage: number = Number(
              (totalSize / currentPageSize).toFixed(0)
            );
            if (
              totalSize < totalPage * currentPageSize ||
              totalSize % currentPageSize === 0
            ) {
              setTotalPages(totalPage);
            } else if (totalSize % currentPageSize !== 0) {
              setTotalPages(totalPage + 1);
            }
            setQuotationData(modifedData);
          } else {
            setQuotationData([]);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          setQuotationData([]);
          appInsights.trackException({
            error,
            properties: {
              name: "In QuotationList.tsx, error while getting all quotations in Quotation.tsx",
            },
          });
        });
  };

  const quotationSelection = useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          const selectedQuotation = quotationSelection.getSelection();
          appInsights.trackEvent({
            name: "In QuotationList.tsx, calling quotationSelection change on Click checkbox",
            properties: {
              quotationSelection: selectedQuotation,
            },
          });
          if (selectedQuotation?.length) {
            const { status } = selectedQuotation[0] as IQuotationResponse;
            if (quotationStatus === QuotationStatus.All) {
              setSelectedQuotationStatus(status);
            }
          } else {
            setSelectedQuotationStatus("");
          }
          setSelectedGridData({ items: selectedQuotation });
        },
        selectionMode: SelectionMode.multiple,
      }),
    [quotationData]
  );

  const onClickNewQuotation = () => {
    // Start quote session tracking
    QuoteSessionTracker.startSession();
    // Clear any previous activity timing data when starting new quote session
    ActivityTimingTracker.clearTimings();
    console.log('🆕 Started new quote creation session - cleared previous activity timing data');
    
    appInsights.trackEvent({
      name: "In QuotationList.tsx, Clicked Create Quotation button to create new quotation.",
    });
    setQuotationInfo({
      ...InitialQuotationInfo,
      quoteLineItems: [],
      quoteLineItemsClone: [],
      standaloneItems: [],
      standaloneItemsClone: [],
      bundles: [],
      bundleItems: [],
      bundleItemsClone: [],
    });
    navigate(window.location, {
      replace: true,
      state: {
        searchText: "",
        actionName: NEW_ACTION,
      },
    });
    navigate("/RecentCustomers", {
      state: { actionName: NEW_ACTION },
    });
  };

  const onClickEditQuotation = () => {
    if (selectedGridData?.items?.length) {
      const { id, customerAccount, partitionKey } = selectedGridData
        .items[0] as IQuotationResponse;
      
      // Track the edit quote session time BEFORE starting new session
      // This tracks the time in any existing session when user clicks Edit
      QuoteSessionTracker.setEditQuoteSessionTime();
      
      // Start quote session tracking for edit
      QuoteSessionTracker.startSession();
      // Clear any previous activity timing data when starting new quote session
      ActivityTimingTracker.clearTimings();
      console.log('🆕 Started new quote edit session - cleared previous activity timing data');
      
      appInsights.trackEvent({
        name: "In QuotationList.tsx, Clicked Update Quotation button to update selected quotation.",
        properties: { quotationId: id },
      });
      setQuotationInfo({
        ...InitialQuotationInfo,
        quoteLineItems: [],
      });
      navigate("/EditQuotation", {
        state: {
          actionName: EDIT_ACTION,
          customerId: customerAccount,
          quotationId: id,
          partitionKey,
        },
      });
    }
  };

  const onClickCustomerAccountDetail = (customerId: any) => {
    CustomerService.getCustomerAccountsById(customerId?.toString())
      .then((res) => {
        if (res !== undefined) {
          appInsights.trackEvent({
            name: "In AppHeader.tsx, customer name has been obtained successfully.",
            properties: { customerId },
          });
          navigate("/CustomerEditContact", { state: res.data.data });
        }
      })
      .catch((error) => {
        appInsights.trackException({
          error,
          properties: {
            name: "In AppHeader.tsx, error while getting customer name.",
          },
        });
      });
  };

  const onClickQuotationDetail = () => {
    if (selectedGridData?.items[0]?.customerId) {
      const { id, partitionKey } = selectedGridData
        .items[0] as IQuotationResponse;
      appInsights.trackEvent({
        name: "In QuotationList.tsx, Clicked Detail Quotation button to get details of selected quotation.",
        properties: { quotationId: id },
      });
      navigate("/QuotationDetail", { state: { searchText: id, partitionKey } });
    }
  };

  const onClickSignQuote = () => {
    if (selectedGridData.items && selectedGridData.items.length > 0 && parseFloat(selectedGridData.items[0].total)) {
      hasSignedSuccefully && setHasSignedSuccessfully(false);
      openPanel && openPanel();
    } else {
      setQuotationFailureMessage({
        message: `${selectedGridData.items[0].quotationNum} quotation should contain at least 1 or more line items, having order amount greater than zero.`,
        showMessage: true,
      });
    }
  };
  
  // Handle column header click to show dropdown
  const handleColumnHeaderClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    ev.preventDefault();
    ev.stopPropagation();
    columnToEdit.current = column;
    setColumnDropdownElement(ev.currentTarget);
  };

  // This function is no longer needed as we've implemented keyboard handling via event listeners in useEffect
  // Keep this stub for possible future use or explicit event handlers on column components
  const handleColumnHeaderKeyDown = (ev: React.KeyboardEvent<HTMLElement>, column: IColumn): void => {
    if (ev.key === 'Enter' || ev.keyCode === KeyCodes.enter) {
      ev.preventDefault();
      ev.stopPropagation();
      columnToEdit.current = column;
      setColumnDropdownElement(ev.currentTarget);
    }
  };
  
  // Function to directly resize columns without dialog
  const resizeColumnDirectly = (column: IColumn, widthChange: number): void => {
    // Get the current width of the column (default to 100 if not set)
    const currentWidth = column.currentWidth || 100;
    const newWidth = Math.max(50, currentWidth + widthChange);
    
    // Update our local state first - this is critical
    const updatedColumns = updatedList.map((col: any) => {
      if (col.key === column.key) {
        return { ...col, currentWidth: newWidth, width: newWidth };
      }
      return col;
    });
    
    // Apply the new columns list
    setUpdatedList(updatedColumns);
    
    // Get reference to the grid component
    const detailsList = detailsListRef.current;
    
    if (detailsList) {
      // Try to use the direct update method if available
      if (detailsList.updateColumn) {
        detailsList.updateColumn(column, { width: newWidth });
      }
      
      // Force a re-render of the grid with the new column widths
      setTimeout(() => {
        if (detailsList.forceUpdate) {
          detailsList.forceUpdate();
        }
      }, 100); // Increased timeout for better reliability
      
      appInsights.trackEvent({
        name: "In QuotationList.tsx, column resized directly using keyboard",
        properties: { 
          columnName: column.name, 
          newWidth: newWidth,
          widthChange: widthChange
        },
      });
      
      // Show feedback to the user
      setQuotationSuccessMessage({
        message: `${column.name} column width adjusted to ${newWidth}px`,
        showMessage: true,
      });
      
      // Auto-hide the message after 2 seconds
      setTimeout(() => {
        setQuotationSuccessMessage(InitialStateOfResponseMessage);
      }, 2000);
    }
  };

  React.useMemo(() => {
    {
      const columnsList = [
        {
          fieldName: "quotationNum",
          key: "quotationNum",
          name: "Quotation ID",
          type: "link",
          url: "/QuotationDetail",
          linkProps: {
            onClick: (item: any) => {
              onClickQuotationDetail();
            },
            style: { textDecoration: "underline" },
          },
          isResizable: true,
          maxWidth: 120,
          ariaLabel: "Quotation ID column. Press Enter to resize.",
          onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Quotation ID")
        },
        {
          fieldName: "msxOpportunityId",
          key: "msxOpportunityId",
          name: "MSX Opportunity ID",
          type: "string",
          isResizable: true,
          maxWidth: 150,
          ariaLabel: "MSX Opportunity ID column. Press Enter to resize.",
          onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("MSX Opportunity ID"),
          onRender(item: any) {
            return (
              <span style={CustomSpanForListing}>
                {item.msxOpportunityId || "-"}
              </span>
            );
          },
        },
        {
          fieldName: "customerName",
          key: "CustomerName",
          name: "Customer Name",
          type: "string",
          isResizable: true,
          ariaLabel: "Customer Name column. Press Enter to resize.",
          onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Customer Name")
        },
        {
          fieldName: "customerAccount",
          key: "CustomerAccount",
          name: "Customer Account",
          type: "link",
          url: "/CustomerEditContact",
          linkProps: {
            onClick: (item: any) =>
              onClickCustomerAccountDetail(item.target.innerHTML),
            style: { textDecoration: "underline" },
          },
          isResizable: true,
          ariaLabel: "Customer Account column. Press Enter to resize.",
          onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Customer Account")
        },
        {
          fieldName: "total",
          key: "Total",
          name: "Order Amount",
          type: "currency",
          isResizable: true,
          ariaLabel: "Order Amount column. Press Enter to resize.",
          onRender(item: any) {
            return item.total ? (
              <span style={CustomSpanForListing}>
                {formattedPrice(item.total.toLocaleString("en-US"))}
              </span>
            ) : (
              <span style={CustomSpanForListing}>{parseFloat("0.00")}</span>
            );
          },
          onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Order Amount")
        },
        {
          fieldName: "country",
          key: "Country",
          name: "Country",
          type: "string",
          isResizable: true,
          ariaLabel: "Country column. Press Enter to resize.",
          onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Country")
        },
        {
          fieldName: "createdAt",
          key: "createdAt",
          name: "Created On",
          type: "string",
          isResizable: true,
          ariaLabel: "Created On column. Press Enter to resize.",
          onRender(item: any) {
            return <span style={CustomSpanForListing}>{formatDateDDMMYYYY(item.createdAt)}</span>;
          },
          onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Created On")
        },
        // Conditionally include Created By column for everyone except Advocate
        ...(userRole !== UserRoles.Advocate
          ? [
              {
                fieldName: "createdByName",
                key: "CreatedBy",
                name: "Created By",
                type: "string",
                isResizable: true,
                ariaLabel: "Created By column. Press Enter to resize.",
                onRender(item: any) {
                  // Ensure the text is selectable (userSelect) and shows text cursor
                  const selectableStyle = { ...CustomSpanForListing, userSelect: 'text' as const, cursor: 'text' };
                  return <span style={selectableStyle}>{item.createdByName || ""}</span>;
                },
                onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Created By"),
              },
            ]
          : []),
      ];

      switch (quotationStatus) {
        case QuotationStatus.Confirmed: {
          const filteredColumns = [
            ...columnsList,
            {
              fieldName: "signedAt",
              key: "signedAt",
              name: "Signed On",
              type: "string",
              isResizable: true,
              onRender(item: any) {
                return <span style={CustomSpanForListing}>{formatDateDDMMYYYY(item.signedAt)}</span>;
              },
              onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Signed On"),
            },
          ];
          setUpdatedList(filteredColumns);
          break;
        }
        case QuotationStatus.Cancelled: {
          const filteredColumns = [
            ...columnsList,
            {
              fieldName: "cancelAt",
              key: "CancelAt",
              name: "Cancelled On",
              type: "string",
              isResizable: true,
              onRender(item: any) {
                return <span style={CustomSpanForListing}>{formatDateDDMMYYYY(item.cancelAt)}</span>;
              },
              onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Cancelled On"),
            },
          ];
          setUpdatedList(filteredColumns);
          break;
        }
        case QuotationStatus.Expired: {
          setUpdatedList([
            ...columnsList,
            {
              fieldName: "expireAt",
              key: "ExpireAt",
              name: "Expired On",
              type: "string",
              isResizable: true,
              onRender(item: any) {
                return <span style={CustomSpanForListing}>{formatDateDDMMYYYY(item.expireAt)}</span>;
              },
              onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Expired On"),
            },
          ]);
          break;
        }
        case QuotationStatus.Draft: {
          setUpdatedList([
            ...columnsList,
            {
              fieldName: "expireAt",
              key: "ExpireAt",
              name: "Expires On",
              type: "string",
              isResizable: true,
              onRender(item: any) {
                return <span style={CustomSpanForListing}>{formatDateDDMMYYYY(item.expireAt)}</span>;
              },
              onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Expires On"),
            },
          ]);
          break;
        }
        default:
          setUpdatedList([
            ...columnsList,
            {
              fieldName: "expireAt",
              key: "ExpireAt",
              name: "Expires On",
              type: "string",
              isResizable: true,
              onRender(item: any) {
                return <span style={CustomSpanForListing}>{formatDateDDMMYYYY(item.expireAt)}</span>;
              },
              onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Expires On"),
            },
            {
              fieldName: "quotationStatus",
              key: "QuotationStatus",
              name: "Status",
              type: "string",
              isResizable: true,
              onRenderHeader: (column?: IColumn) => renderColumnHeaderWithDropdown("Status"),
            },
          ]);
      }
    }
  }, [quotationStatus, JSON.stringify(selectedGridData?.items)]);

  const onClickUpdateQuotationStatus = (quoteStatus: string) => {
    if (selectedGridData?.items?.length) {
      const { id, status, partitionKey, total } = selectedGridData
        .items[0] as IQuotationResponse;
      if (
        (quoteStatus === QuotationStatus.Confirmed && total) ||
        quoteStatus === QuotationStatus.Cancelled
      ) {
        QuotationService.updateQuotationStatus(id, partitionKey, quoteStatus)
          .then((res) => {
            if (res.data.code === 200) {
              appInsights.trackEvent({
                name: "In QuotationList.tsx, quotation status has been updated successfully.",
                properties: { status: status },
              });
              const updatedQuotes = quotationData.filter(
                (quote: IQuotationResponse) => quote.id !== id
              );
              setQuotationData(updatedQuotes);
              setQuotationSuccessMessage({
                message: `Quotation has been ${quoteStatus} successfully`,
                showMessage: true,
              });
            } else {
              appInsights.trackTrace({
                message:
                  "In QuotationList.tsx, something went wrong while updating quotation status.",
              });
              const updatedQuotes = quotationData.map(
                (quote: IQuotationResponse) => {
                  return quote.id !== id
                    ? quote
                    : {
                        ...quote,
                        status: res.data.data.status,
                      };
                }
              );
              setQuotationData(updatedQuotes as never[]);
              setQuotationFailureMessage({
                message: res.data.message,
                showMessage: true,
              });
            }
          })
          .catch((error) => {
            setQuotationFailureMessage({
              message: `${quoteStatus} quotation process has been failed due to some issue.`,
              showMessage: true,
            });
            appInsights.trackException({
              error,
              properties: {
                name: "In QuotationList.tsx, error while updating quotation status.",
              },
            });
          });
      } else {
        setQuotationFailureMessage({
          message: `${quoteStatus} quotation should contain at least 1 or more line items.`,
          showMessage: true,
        });
      }
    }
  };

  // Helper to convert DD-MM-YYYY to MM/DD/YYYY
  const convertDDMMYYYYtoMMDDYYYY = (input: string): string => {
      // Match DD-MM-YYYY, D-M-YYYY, DD/MM/YYYY, D/M/YYYY
      const match = input.match(/\b(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})\b/);
    if (match) {
      const [, dd, mm, yyyy] = match;
      const day = dd.padStart(2, '0');
      const month = mm.padStart(2, '0');
      return input.replace(match[0], `${month}/${day}/${yyyy}`);
    }
    return input;
  };

  const sendSearchTextToParent = (text: string) => {
    // Convert DD-MM-YYYY to MM/DD/YYYY if present
    const processedText = convertDDMMYYYYtoMMDDYYYY(text);
    setSearchText(processedText); // Ensure state is updated for payload
    if (props.onSearchTextChange) {
      props.onSearchTextChange(processedText);
    }
    setSearchResponseMessage("");
    setTotalPages(DefaultTotalPages);
    setCurrentPage(DefaultCurrentPage);
    setCurrentPageSize(DefaultCurrentPageSize);
    setSelectedGridData({ items: [] });
    quotationSelection.setAllSelected(false);
  };

  const hostStyles: Partial<ITooltipHostStyles> = {
    root: { display: "inline-block" },
  };
  
  // Helper function to render column headers with dropdown icon
  const renderColumnHeaderWithDropdown = (columnName: string) => {
    return (
      <span>
        {columnName}
        <Icon iconName="ChevronDown" style={{ fontSize: '8px', marginLeft: '5px', marginBottom: '2px' }} />
      </span>
    );
  };
  
  // Track the current sort column and direction
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Function to sort column data
  const sortColumnData = (column: IColumn, direction: 'asc' | 'desc'): void => {
    if (!column || !column.fieldName) return;
    
    // Update sort column and direction
    setSortColumn(column.fieldName);
    setSortDirection(direction);
    
    // Update the column display to show it's sorted
    const updatedColumns = updatedList.map((col: any) => {
      if (col.fieldName === column.fieldName) {
        return { 
          ...col, 
          isSorted: true,
          isSortedDescending: direction === 'desc' 
        };
      }
      return { ...col, isSorted: false };
    });
    setUpdatedList(updatedColumns);
    
    // Apply the sort to the data
    const sortedData = [...quotationData].sort((a: any, b: any) => {
      const fieldName = column.fieldName || '';
      
      const aValue = a[fieldName];
      const bValue = b[fieldName];
      
      // Handle strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return direction === 'asc' ? 
          aValue.localeCompare(bValue) : 
          bValue.localeCompare(aValue);
      }
      
      // Handle numbers and other types
      if (direction === 'asc') {
        return (Number(aValue) || 0) - (Number(bValue) || 0);
      } else {
        return (Number(bValue) || 0) - (Number(aValue) || 0);
      }
    });
    
    setQuotationData(sortedData as never[]);
    
    // Show feedback message
    const sortDirectionText = direction === 'asc' ? '(A to Z)' : '(Z to A)';
    const sortMethodText = "using " + (direction === 'asc' ? 'Alt+Up Arrow' : 'Alt+Down Arrow');
    
    setQuotationSuccessMessage({
      message: `Sorted by ${column.name} ${sortDirectionText}`,
      showMessage: true,
    });
    
    // Log event for tracking
    appInsights.trackEvent({
      name: "In QuotationList.tsx, column sorted",
      properties: { 
        columnName: column.name, 
        sortDirection: direction,
        sortMethod: "keyboard shortcut"
      },
    });
    
    setTimeout(() => {
      setQuotationSuccessMessage(InitialStateOfResponseMessage);
    }, 2000);
  };

  return (
    <>
      {quotationSuccessMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() =>
            setQuotationSuccessMessage(InitialStateOfResponseMessage)
          }
        >
          {quotationSuccessMessage.message}
        </MessageBar>
      )}
      {quotationFailureMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() =>
            setQuotationFailureMessage(InitialStateOfResponseMessage)
          }
        >
          {quotationFailureMessage.message}
        </MessageBar>
      )}
      {isPanelOpen && (
        <SignQuotationComponent
          isPanelOpen={isPanelOpen}
          dismissPanel={dismissPanel}
          quotationId={selectedGridData.items[0].id}
          quotationNumber={selectedGridData.items[0].quotationNum}
          partitionKey={selectedGridData.items[0].partitionKey}
          customerName={selectedGridData.items[0].customerName}
          createdAt={selectedGridData.items[0].createdAt}
          expireAt={selectedGridData.items[0].expireAt}
          paymentType={selectedGridData.items[0].paymentType}
          customerPurchaseOrderNumber={selectedGridData.items[0].poNumber}
          setHasSignedSuccessfully={setHasSignedSuccessfully}
          setQuotationErrorMessage={setQuotationFailureMessage}
          standaloneItems={selectedGridData.items[0].standaloneItems}
          bundleItems={selectedGridData.items[0].bundleItems}
        />
      )}
      <Stack
        horizontal
        horizontalAlign="space-between"
        style={{
          minHeight: "50px",
          minWidth: "320px",
        }}
      >
        <Stack horizontal>
          {(quotationStatus === QuotationStatus.All ||
            quotationStatus === QuotationStatus.Draft) &&
            genericRole !== -1 && (
              <Stack horizontal>
                <ActionButton 
                  iconProps={AddIcon} 
                  onClick={onClickNewQuotation}
                >
                  Add
                </ActionButton>
                <ActionButton
                  iconProps={EditIcon}
                  disabled={
                    (quotationStatus !== QuotationStatus.Draft &&
                      selectedQuotationStatus !== QuotationStatus.Draft) ||
                    selectedGridData.items.length !== 1
                  }
                  onClick={onClickEditQuotation}
                >
                  Edit
                </ActionButton>
                {isOpenModal && (
                  <DialogComponent
                    isOpenModal
                    closeModal={closeModal}
                    title={`Confirm Quote Cancellation`}
                    description={`Are you sure you want to cancel the selected quote ${selectedGridData.items[0].quotationNum}?`}
                    onClickYes={() =>
                      onClickUpdateQuotationStatus(QuotationStatus.Cancelled)
                    }
                    yesButtonLabel="Yes"
                    noButtonLabel="No"
                    onClickNo={closeModal}
                  />
                )}
                {(quotationStatus === QuotationStatus.Draft ||
                  selectedQuotationStatus === QuotationStatus.Draft) && (
                    <ActionButton
                      iconProps={CancelIcon}
                      disabled={selectedGridData.items.length !== 1}
                      onClick={() => {
                        if (
                          userRole === UserRoles.Admin ||
                          userRole === UserRoles.Manager ||
                          (userRole === UserRoles.Advocate &&
                            (selectedGridData?.items[0].createdBy ===
                              userInfo.id ||
                              selectedGridData?.items[0].createdBy ===
                              userInfo.displayName))
                        ) {
                          openModal();
                        } else {
                          setQuotationFailureMessage({
                            message: `You don't have access to cancel ${selectedGridData.items[0].quotationNum} quote.`,
                            showMessage: true,
                          });
                        }
                      }}
                    >
                      Cancel Quote
                    </ActionButton>
                  )}
                {(quotationStatus === QuotationStatus.Draft ||
                  selectedQuotationStatus === QuotationStatus.Draft) && (
                    <ActionButton
                      iconProps={SignatureIcon}
                      disabled={selectedGridData.items.length !== 1}
                      onClick={onClickSignQuote}
                    >
                      Signed
                    </ActionButton>
                  )}
              </Stack>
            )}
        </Stack>
        <Separator />
        <Stack horizontal>
          <TooltipHost
            content={
              window.innerWidth < 900
                ? (userRole !== UserRoles.Advocate
                    ? "Search by Quotation ID, Customer Name, Customer Account, Country, Created By or Created On(dd-mm-yyyy)"
                    : "Search by Quotation ID, Customer Name, Customer Account, Country or Created On(dd-mm-yyyy)")
                : ""
            }
            delay={TooltipDelay.zero}
            directionalHint={DirectionalHint.bottomCenter}
            styles={hostStyles}
          >
            <SearchBox
              styles={
                window.innerWidth < 900
                  ? searchBoxIconWidthStyles
                  : searchBoxWidthStyles
              }
              placeholder={
                window.innerWidth > 900
                  ? (userRole !== UserRoles.Advocate
                      ? "Search by Quotation ID, MSX Opportunity ID, Customer Name, Customer Account, Country, Created By or Created On(dd-mm-yyyy)"
                      : "Search by Quotation ID, MSX Opportunity ID, Customer Name, Customer Account, Country or Created On(dd-mm-yyyy)")
                  : ""
              }
              aria-label={searchResponseMessage}
              onChange={(_, newValue) => {
                setSearchTextTemp(newValue ?? "");                
              }}
              onSearch={(newValue) => {
                setSearchText(newValue);
                sendSearchTextToParent(newValue);
              }}
              onClear={(newValue) => {
                setSearchText("");
                sendSearchTextToParent("");
              }}
            />
          </TooltipHost>
          <Stack
            role="button"
            tabIndex={0}
            aria-label="Search"
            onClick={handleSearchSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.code === "Space") {
                e.preventDefault();
                handleSearchSubmit();
              }
            }}
          >
            <Icon
              iconName="Search"
              style={searchIconStyle}
            />
          </Stack>


          <Stack
            aria-live="polite"
            style={{ position: "absolute", left: "-9999px", opacity: "0" }}
          >
            {searchResponseMessage}
          </Stack>
        </Stack>
      </Stack>

      {isLoading && <Spinner size={SpinnerSize.large} />}
      {!isLoading && quotationData?.length > 0 && (
        <CoherenceDataGrid
          componentRef={detailsListRef}
          styles={{ root: { minHeight: "250px" } }}
          ariaLabelForSelectAllCheckbox="select all checkbox"
          checkButtonAriaLabel="checkbox"
          compactMode="compact"
          data={quotationData}
          isLoading={isLoading}
          isScrollable
          initialSortedColumn={sortColumn}
          invertInitialSort={sortDirection === 'desc'}
          isSortable={true}
          listConfig={updatedList}
          selection={quotationSelection}
          selectionMode={SelectionMode.multiple}
          selectionPreservedOnEmptyClick={true}
          setKey={`DATA_GRID_${Date.now()}_${sortColumn}_${sortDirection}_${JSON.stringify(updatedList.map((col: any) => `${col.key}_${col.currentWidth || 0}`))}`}
          onColumnClick={handleColumnHeaderClick}
          data-automation-id="quotation-data-grid"
        />
      )}

      {!isLoading && quotationData.length > 0 && (
        <Stack style={{ marginTop: 15, marginBottom: 15 }}>
          <PaginationComponent
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            currentPageSize={currentPageSize}
            setCurrentPageSize={setCurrentPageSize}
            totalPages={totalPages}
          />
            </Stack>
      )}

      {!isLoading && quotationData?.length === 0 && (
        <Stack horizontalAlign="center" style={{ marginTop: "10vw" }}>
          <h4>No Record Found</h4>
        </Stack>
      )}
      
      {/* Column Resize Dialog */}
      {/* Accessible instructions for screen reader users */}
      <div aria-live="polite" className="srOnly" style={{ position: 'absolute', width: '1px', height: '1px', padding: '0', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: '0' }}>
        Use Tab key to navigate to column headers. Press Enter on a column header to open column options. Select Sort A to Z or Sort Z to A to sort the column. Select Resize to adjust column width. Use arrow keys to navigate between columns. For direct resizing, use Shift+Left Arrow to decrease width or Shift+Right Arrow to increase width. For direct sorting, use Alt+Up Arrow to sort A to Z or Alt+Down Arrow to sort Z to A.
      </div>
      
      {/* Current sort status for screen readers */}
      <div aria-live="assertive" className="srOnly" style={{ position: 'absolute', width: '1px', height: '1px', padding: '0', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: '0' }}>
        {sortColumn && `Table sorted by ${sortColumn} in ${sortDirection === 'asc' ? 'ascending' : 'descending'} order. Use Alt+Up Arrow or Alt+Down Arrow on column headers to change sorting.`}
      </div>
      
      {/* Hidden technical element for debugging keyboard events */}
      <div id="keyboard-sort-debug" aria-hidden="true" style={{ display: 'none' }}>
        Last keyboard sort attempt: None
      </div>
      
      {/* Column Resize Dialog */}
      <Dialog
        hidden={isResizeDialogHidden}
        onDismiss={() => setIsResizeDialogHidden(true)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Resize Column",
          closeButtonAriaLabel: "Close",
          subText: columnToEdit.current ? `Enter desired width in pixels for the ${columnToEdit.current.name} column:` : "Enter desired column width in pixels:",
        }}
        modalProps={{
          titleAriaId: "ResizeColumnDialog",
          subtitleAriaId: "ResizeColumnDialogSubtitle",
          isBlocking: false,   
          styles: { main: { maxWidth: 450 } },
        }}
      >
        <TextField
          componentRef={textfieldRef}
          ariaLabel="Enter column width in pixels"
          placeholder="Enter width in pixels"
          type="number"
          min={50}
          defaultValue={columnToEdit.current?.currentWidth?.toString() || "100"}
          autoFocus={true}
          description="Press Enter to confirm or Escape to cancel"
          onKeyDown={(ev) => {
            if (ev.key === "Enter" || ev.keyCode === KeyCodes.enter) {
              confirmResize();
            } else if (ev.key === "Escape" || ev.keyCode === KeyCodes.escape) {
              setIsResizeDialogHidden(true);
            }
          }}
        />
        <DialogFooter>
          <PrimaryButton onClick={confirmResize} text="Resize" data-automation-id="resize-column-button" />
          <DefaultButton onClick={() => setIsResizeDialogHidden(true)} text="Cancel" />
        </DialogFooter>
      </Dialog>
      
      {/* Column Header Dropdown Menu */}
      {columnDropdownElement && (
        <ContextualMenu
          items={[
            {
              key: 'sortAsc',
              text: 'Sort A to Z',
              iconProps: { iconName: 'SortUp' },
              onClick: () => {
                if (columnToEdit.current) {
                  sortColumnData(columnToEdit.current, 'asc');
                  setColumnDropdownElement(null);
                }
              }
            },
            {
              key: 'sortDesc',
              text: 'Sort Z to A',
              iconProps: { iconName: 'SortDown' },
              onClick: () => {
                if (columnToEdit.current) {
                  sortColumnData(columnToEdit.current, 'desc');
                  setColumnDropdownElement(null);
                }
              }
            },
            {
              key: 'resize',
              text: 'Resize',
              iconProps: { iconName: 'ColumnRightTwoThirdsEdit' },
              onClick: () => {
                setIsResizeDialogHidden(false);
                // Set the focus to the dialog when it opens
                setTimeout(() => {
                  if (textfieldRef.current) {
                    textfieldRef.current.focus();
                  }
                }, 0);
              }
            }
          ]}
          target={columnDropdownElement}
          onDismiss={() => setColumnDropdownElement(null)}
          directionalHint={DirectionalHint.bottomCenter}
          shouldFocusOnMount={true}
        />
      )}
    </>
  );
  
  // Function to handle column resize
  function confirmResize() {
    if (textfieldRef.current && columnToEdit.current) {
      const width = Number(textfieldRef.current.value);
      if (width && width >= 50) {
        // Update the column width in our local state first
        const updatedColumns = updatedList.map((col: any) => {
          if (col.key === columnToEdit.current!.key) {
            return { ...col, currentWidth: width, width: width };
          }
          return col;
        });
        
        // Set the updated column list which will trigger the grid to rerender
        setUpdatedList(updatedColumns);
        
        // Get reference to the DetailsList component
        const detailsList = detailsListRef.current;
        
        // For CoherenceDataGrid, we need to make sure we update its internal state
        if (detailsList) {
          // Try direct update if available
          if (detailsList.updateColumn) {
            detailsList.updateColumn(columnToEdit.current, { width });
          }
          
          // Force a re-render of the grid with the new column widths
          setTimeout(() => {
            if (detailsList.forceUpdate) {
              detailsList.forceUpdate();
            }
          }, 0);
          
          appInsights.trackEvent({
            name: "In QuotationList.tsx, column resized using resize button",
            properties: { 
              columnName: columnToEdit.current.name, 
              newWidth: width 
            },
          });
          
          // Show feedback to the user
          setQuotationSuccessMessage({
            message: `${columnToEdit.current.name} column width adjusted to ${width}px`,
            showMessage: true,
          });
          
          // Auto-hide the message after 2 seconds
          setTimeout(() => {
            setQuotationSuccessMessage(InitialStateOfResponseMessage);
          }, 2000);
        }
        
        // Hide the dialog
        setIsResizeDialogHidden(true);
      }
    }
  }
};
